import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Award,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Clock,
  Grid3x3,
  List,
  MapPin,
  Play,
  Search,
  Star,
  Target
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Shared Components
import SimpleSelect from '@/components/ui/SimpleSelect';

// Services
import { learnerLearningPathService, LearningPathWithProgress } from '@/services/learner-learning-path.service';
import { reviewService } from '@/services/review.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_LEARNING_PATHS_PERMISSION } from '@/constants/constant-permissions';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';

// Compact Learning Path Card Component
const CompactLearningPathCard = ({
  path,
  onAction,
  onRatingClick,
  onClaimCertificate,
  canRead,
  canWrite,
  certificateNumber
}: {
  path: LearningPathWithProgress;
  onAction: (pathId: string, action: 'view' | 'continue') => void;
  onRatingClick: (pathId: string) => void;
  onClaimCertificate?: (certificateNumber: string) => void;
  canRead: boolean;
  canWrite: boolean;
  certificateNumber?: string;
}) => {
  const isOverdue = path.deadline_date && new Date(path.deadline_date) < new Date();
  const daysLeft = path.deadline_date ?
    Math.ceil((new Date(path.deadline_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-200 overflow-hidden group flex flex-col h-full">
      {/* Thumbnail */}
      <div className="relative h-36 bg-gradient-to-br from-brand-green-50 to-brand-emerald-100 overflow-hidden flex-shrink-0">
        {path.thumbnail_image_url || path.banner_image_url ? (
          <img
            src={path.thumbnail_image_url || path.banner_image_url}
            alt={path.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <MapPin className="w-12 h-12 text-brand-green-400" />
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2 flex gap-1 flex-wrap">
          {path.is_mandatory && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              Mandatory
            </span>
          )}
          {path.status === 'COMPLETED' && (
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Completed
            </span>
          )}
        </div>

        {/* Progress Bar */}
        {/* {path.status === 'IN_PROGRESS' && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm">
            <div className="h-1 bg-white/30">
              <div
                className="h-full bg-white transition-all duration-300"
                style={{ width: `${path.progress_percentage}%` }}
              />
            </div>
          </div>
        )} */}
      </div>

      {/* Content - Flex grow to push buttons to bottom */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Title - Fixed 2 lines height */}
        <h3 className="font-medium text-sm text-gray-900 line-clamp-2 mb-2 group-hover:text-brand-green-600 transition-colors min-h-[2.5rem]">
          {path.title}
        </h3>

        {/* Description - 2 lines */}
        <p className="text-xs text-gray-600 line-clamp-2 mb-2 min-h-[2rem]">
          {path.description || 'No description available'}
        </p>

        {/* Meta Info */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2 flex-wrap">
          <span className="flex items-center gap-1">
            <BookOpen className="w-3 h-3" />
            {path.total_courses} courses
          </span>
          {/* Rating Display - Clickable */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRatingClick(path.learning_path_id);
            }}
            className="flex items-center gap-1 hover:bg-yellow-50 px-1.5 py-0.5 rounded-md transition-colors cursor-pointer"
            title="View reviews"
          >
            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium text-gray-700">{path.rating?.toFixed(1) || '0.0'}</span>
            <span className="text-gray-400">• {path.review_count || 0} ratings</span>
          </button>
        </div>

        {/* Status Info */}
        {path.status !== 'NOT_STARTED' && (
          <div className="text-xs text-gray-600 mb-2">
            {path.status === 'COMPLETED' && (
              <span className="text-green-600 font-medium">
                {path.completed_courses} of {path.total_courses} courses completed
              </span>
            )}
            {path.status === 'IN_PROGRESS' && (
              <span>{path.completed_courses} of {path.total_courses} courses completed</span>
            )}
          </div>
        )}

        {/* Deadline and Progress */}
        {path.deadline_date && path.status !== 'COMPLETED' && (
          <div className={`text-xs mb-2 ${isOverdue ? 'text-red-600' : daysLeft && daysLeft <= 7 ? 'text-orange-600' : 'text-gray-500'}`}>
            {isOverdue ? (
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Overdue
              </span>
            ) : (
              <span>Due in {daysLeft} days</span>
            )}
          </div>
        )}

        {/* Progress Bar */}
        {path.status !== 'NOT_STARTED' && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-brand-green-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${path.progress_percentage}%` }}
              />
            </div>
            <span className="text-xs text-gray-600">{path.progress_percentage}%</span>
          </div>
        )}

        {/* Spacer to push buttons to bottom */}
        <div className="flex-grow"></div>

        {/* Action Buttons - Fixed to bottom */}
        <div className="flex flex-col gap-2 mt-auto">
          {/* Claim Certificate Button - Only for completed paths with certificate */}
          {path.status === 'COMPLETED' && certificateNumber && (
            <button
              onClick={() => onClaimCertificate?.(certificateNumber)}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 sm:py-1.5 bg-amber-50 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-xs font-medium"
            >
              <Award className="w-3.5 h-3.5" />
              Claim Certificate
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => canRead && onAction(path.learning_path_id, 'view')}
              disabled={!canRead}
              title={!canRead ? 'You do not have permission to access learning paths' : undefined}
              className={`flex-1 px-2 py-2 sm:py-1.5 border rounded-lg transition-colors text-xs font-medium ${
                canRead
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
              }`}
            >
              View
            </button>
            <button
              onClick={() => canWrite && onAction(
                path.learning_path_id,
                'continue'
              )}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission to access learning paths' : undefined}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 rounded-lg transition-colors text-xs font-medium ${
                canWrite
                  ? 'bg-brand-green-600 text-white hover:bg-brand-green-700'
                  : 'bg-brand-green-600 text-white opacity-50 cursor-not-allowed'
              }`}
            >
              {path.status === 'IN_PROGRESS' ? 'Continue' :
                path.status === 'COMPLETED' ? 'Review' : 'Start'}
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function LearnerLearningPaths() {
  // Permission guard
  usePermissionRouteAccess([
    LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_READ,
    LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE,
  ]);
  const canRead = useHasPermission([LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_READ, LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE]);
  const canWrite = useHasPermission([LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE]);

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'in_progress' | 'completed' | 'not_started' | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastAccessed');

  // Fetch learning paths
  const { data: rawLearningPaths = [], isLoading: pathsLoading } = useQuery({
    queryKey: ['learner-learning-paths'],
    queryFn: () => learnerLearningPathService.getMyLearningPaths(),
  });

  // Fetch dashboard stats
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['learning-path-dashboard-stats'],
    queryFn: () => learnerLearningPathService.getDashboardStats(),
  });

  // Build learning_path_id -> certificate_number map from rawLearningPaths (returned by learning-paths API)
  const lpCertificateMap = (() => {
    const map = new Map<string, string>();
    rawLearningPaths.forEach(path => {
      if (path.certificate_number) {
        map.set(path.learning_path_id, path.certificate_number);
      }
    });
    return map;
  })();

  // Fetch review stats for all learning paths (single API call)
  const { data: reviewStatsMap } = useQuery({
    queryKey: ['learner-learning-path-review-stats-bulk'],
    queryFn: () => reviewService.getBatchLearningPathStats(),
  });

  // Enrich learning paths with review stats
  const learningPaths = rawLearningPaths.map((lp: LearningPathWithProgress) => {
    const stats = reviewStatsMap?.get(lp.learning_path_id);
    return {
      ...lp,
      rating: stats?.avg_rating ?? lp.rating ?? 0,
      review_count: stats?.total_reviews ?? lp.review_count ?? 0,
    };
  });

  const isLoading = pathsLoading || statsLoading;

  // Filter learning paths based on active tab and search
  const filteredPaths = learningPaths.filter((path) => {
    const matchesSearch = searchTerm === '' ||
      path.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      path.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTab = activeTab === 'all' ||
      (activeTab === 'in_progress' && path.status === 'IN_PROGRESS') ||
      (activeTab === 'completed' && path.status === 'COMPLETED') ||
      (activeTab === 'not_started' && path.status === 'NOT_STARTED');

    return matchesSearch && matchesTab;
  });

  // Sort learning paths
  const sortedPaths = [...filteredPaths].sort((a, b) => {
    switch (sortBy) {
      case 'lastAccessed':
        const aDate = a.last_accessed_at || (a as any).updatedAt;
        const bDate = b.last_accessed_at || (b as any).updatedAt;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      case 'enrolledDate':
        const aCreated = (a as any).createdAt;
        const bCreated = (b as any).createdAt;
        if (!aCreated) return 1;
        if (!bCreated) return -1;
        return new Date(bCreated).getTime() - new Date(aCreated).getTime();
      default:
        return 0;
    }
  });

  // Handle actions
  const handleAction = (pathId: string, _action: 'view' | 'continue') => {
    navigate(`/learn/learning-paths/${pathId}`);
  };

  // Handle claim certificate - navigate to public verification page
  const handleClaimCertificate = (certificateNumber: string) => {
    navigate(`/certificate/verify/${certificateNumber}`);
  };

  // Handle rating click - redirect to preview with reviews tab
  const handleRatingClick = (pathId: string) => {
    navigate(`/learn/learning-paths/${pathId}?tab=reviews`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-brand-green-600 to-brand-emerald-600 rounded-xl p-6 animate-pulse">
          <div className="h-8 w-48 bg-white/20 rounded mb-2" />
          <div className="h-4 w-72 bg-white/10 rounded mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white/10 rounded-lg p-3">
                <div className="h-3 w-16 bg-white/20 rounded mb-2" />
                <div className="h-7 w-10 bg-white/20 rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} showImage lines={3} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-green-600 to-brand-emerald-600 rounded-xl p-4 md:p-6 text-white">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 flex items-center gap-2">
              <MapPin className="w-6 h-6 md:w-7 md:h-7" />
              My Learning Paths
            </h1>
            <p className="text-brand-green-100 text-sm md:text-base">
              Track your progress across structured learning journeys
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-green-100 text-xs md:text-sm">Total</p>
            <p className="text-xl md:text-2xl font-bold">
              {learningPaths.length}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-green-100 text-xs md:text-sm">In Progress</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.in_progress_learning_paths || 0}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-green-100 text-xs md:text-sm">Completed</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.completed_learning_paths || 0}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-green-100 text-xs md:text-sm">Total Hours</p>
            <p className="text-xl md:text-2xl font-bold">{Math.round(dashboardStats?.total_time_spent_hours || 0)}h</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max">
            {[
              { key: 'all', label: 'All Learning Paths', icon: MapPin, count: learningPaths.length },
              {
                key: 'in_progress',
                label: 'In Progress',
                icon: Play,
                count: learningPaths.filter(p => p.status === 'IN_PROGRESS').length
              },
              {
                key: 'completed',
                label: 'Completed',
                icon: CheckCircle,
                count: learningPaths.filter(p => p.status === 'COMPLETED').length
              },
              { key: 'not_started', label: 'Not Started', icon: Target, count: learningPaths.filter(p => p.status === 'NOT_STARTED').length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-2 px-3 md:px-6 py-3 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-brand-green-600 text-brand-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters and Search */}
        <div className="p-3 md:p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search learning paths..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 md:pl-10 pr-4 py-2 text-sm md:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1 sm:flex-none sm:w-48">
                <SimpleSelect
                  value={sortBy}
                  onChange={(val) => setSortBy(String(val || 'lastAccessed'))}
                  options={[
                    { value: 'lastAccessed', label: 'Last Accessed' },
                    { value: 'title', label: 'Title' },
                    { value: 'enrolledDate', label: 'Enrolled Date' },
                  ]}
                  placeholder="Sort by"
                  isSearchable={false}
                />
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-brand-green-100 text-brand-green-600' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <Grid3x3 className="w-4 h-4 md:w-5 md:h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-brand-green-100 text-brand-green-600' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <List className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {sortedPaths.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <MapPin className="w-10 h-10 md:w-12 md:h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm md:text-base text-gray-600 px-4">
                {searchTerm ? `No learning paths found matching "${searchTerm}"` :
                 activeTab === 'in_progress' ? "You don't have any learning paths in progress" :
                 activeTab === 'completed' ? "You haven't completed any learning paths yet" :
                 activeTab === 'not_started' ? "You don't have any unstarted learning paths" :
                 "No learning paths assigned yet"}
              </p>
            </div>
          ) : (
            <>
            {/* Phones get one column, where grid and list look the same: always show the grid there. */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 ${viewMode === 'list' ? 'sm:hidden' : ''}`}>
              {sortedPaths.map((path) => (
                <CompactLearningPathCard
                  key={path.learning_path_id}
                  path={path}
                  onAction={handleAction}
                  onRatingClick={handleRatingClick}
                  onClaimCertificate={handleClaimCertificate}
                  canRead={canRead}
                  canWrite={canWrite}
                  certificateNumber={lpCertificateMap?.get(path.learning_path_id)}
                />
              ))}
            </div>
            {viewMode === 'list' && (
            <div className="hidden sm:block space-y-3 md:space-y-4">
              {sortedPaths.map((path) => {
                const isOverdue = path.deadline_date && new Date(path.deadline_date) < new Date();
                const daysLeft = path.deadline_date ?
                  Math.ceil((new Date(path.deadline_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;

                return (
                  <div key={path.learning_path_id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 hover:shadow-md transition-all">
                    <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                      <div className="w-full sm:w-20 h-32 sm:h-20 rounded-lg overflow-hidden bg-gradient-to-br from-brand-green-50 to-brand-emerald-100 flex-shrink-0">
                        {path.thumbnail_image_url ? (
                          <img
                            src={path.thumbnail_image_url}
                            alt={path.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MapPin className="w-8 h-8 md:w-10 md:h-10 text-brand-green-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm md:text-base text-gray-900 truncate">{path.title}</h3>
                            <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{path.description}</p>
                          </div>
                          {path.is_mandatory && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium whitespace-nowrap">
                              Mandatory
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600 mb-3">
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3 h-3 md:w-4 md:h-4" />
                            {path.completed_courses}/{path.total_courses} courses
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 md:w-4 md:h-4" />
                            {learnerLearningPathService.formatTimeSpent(path.total_time_spent_seconds)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3 md:w-4 md:h-4" />
                            {path.progress_percentage}% complete
                          </span>
                          {path.deadline_date && (
                            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : daysLeft && daysLeft <= 7 ? 'text-orange-600' : ''}`}>
                              <AlertCircle className="w-3 h-3 md:w-4 md:h-4" />
                              {isOverdue ? 'Overdue' : `Due in ${daysLeft} days`}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                          <div className="flex-1">
                            {path.status === 'COMPLETED' && (
                              <div className="flex items-center gap-2 text-xs md:text-sm text-green-600 font-medium">
                                <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                                Completed
                              </div>
                            )}
                            {path.status === 'IN_PROGRESS' && (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-brand-green-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${path.progress_percentage}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600">{path.progress_percentage}%</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {path.status === 'COMPLETED' && lpCertificateMap?.get(path.learning_path_id) && (
                              <button
                                onClick={() => handleClaimCertificate(lpCertificateMap.get(path.learning_path_id)!)}
                                className="flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100"
                              >
                                <Award className="w-4 h-4" />
                                Claim Certificate
                              </button>
                            )}
                            <button
                              onClick={() => canRead && handleAction(path.learning_path_id, 'view')}
                              disabled={!canRead}
                              title={!canRead ? 'You do not have permission to access learning paths' : undefined}
                              className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm ${
                                canRead
                                  ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                  : 'bg-white border border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
                              }`}
                            >
                              View
                            </button>
                            <button
                              onClick={() => canWrite && handleAction(path.learning_path_id, 'continue')}
                              disabled={!canWrite}
                              title={!canWrite ? 'You do not have permission to access learning paths' : undefined}
                              className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm ${
                                canWrite
                                  ? 'bg-brand-green-600 text-white hover:bg-brand-green-700'
                                  : 'bg-brand-green-600 text-white opacity-50 cursor-not-allowed'
                              }`}
                            >
                              {path.status === 'IN_PROGRESS' ? 'Continue' :
                                path.status === 'COMPLETED' ? 'Review' : 'Start'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
